"use client";

import { useState, useTransition } from "react";

import Link from "next/link";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  BugIcon,
  CpuIcon,
  LightbulbIcon,
  MessageCircleQuestion,
} from "lucide-react";
import { useForm } from "react-hook-form";

import { Tables } from "@kit/supabase/database";
import { Alert, AlertDescription, AlertTitle } from "@kit/ui/alert";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@kit/ui/alert-dialog";
import { Button } from "@kit/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@kit/ui/form";
import { If } from "@kit/ui/if";
import { Input } from "@kit/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@kit/ui/select";
import { Textarea } from "@kit/ui/textarea";
import { Trans } from "@kit/ui/trans";

import { InsertIssueSchema } from "../lib/schema/insert-issue.schema";
import { submitIssueAction } from "../lib/server/server-actions";

export function IssueSubmissionDialog(
  props: React.PropsWithChildren<{
    disabled: boolean;
  }>,
) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>{props.children}</AlertDialogTrigger>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <Trans i18nKey={"roadmap:submitPost"} defaults={"Submit Post"} />
          </AlertDialogTitle>

          <AlertDialogDescription>
            <Trans
              i18nKey={"roadmap:fillOutForm"}
              defaults={
                "Fill out the form below to submit your post to the community"
              }
            />
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div>
          <IssueSubmissionForm
            disabled={props.disabled}
            onSubmit={() => setIsOpen(false)}
          />
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function IssueSubmissionForm(props: {
  onSubmit: (data: { title: string; description: string }) => void;
  disabled: boolean;
}) {
  const [pending, startTransition] = useTransition();

  const form = useForm({
    resolver: zodResolver(InsertIssueSchema),
    disabled: props.disabled,
    defaultValues: {
      title: "",
      description: "",
      type: "feature" as Tables<"issues">["type"],
    },
  });

  return (
    <Form {...form}>
      <form
        className={"flex flex-col space-y-4"}
        onSubmit={form.handleSubmit((data) => {
          startTransition(async () => {
            await submitIssueAction(data);

            props.onSubmit(data);
          });
        })}
      >
        <If condition={props.disabled}>
          <Alert variant={"default"} className={"my-8"}>
            <AlertTitle className={"flex items-center gap-2 text-xl"}>
              Please{" "}
              <Link href="/auth/sign-in" className="text-primary underline">
                sign in
              </Link>{" "}
              to submit a feature request
            </AlertTitle>

            <AlertDescription>
              <Trans
                i18nKey={"roadmap:pleaseSignInDescription"}
                defaults={
                  "You must be signed in to submit a feature request to the community"
                }
              />
            </AlertDescription>
          </Alert>
        </If>

        <If condition={!props.disabled}>
          <FormField
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={"roadmap:type"} defaults={"Type"} />
                </FormLabel>

                <FormControl>
                  <Select
                    disabled={props.disabled}
                    value={field.value}
                    onValueChange={(value) => {
                      form.setValue(
                        field.name,
                        value as Tables<"issues">["type"],
                        {
                          shouldValidate: true,
                        },
                      );
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value={"feature"}>
                        <span className={"flex items-center gap-1"}>
                          <LightbulbIcon className={"h-4 text-yellow-500"} />
                          <span>
                            <Trans
                              i18nKey={"roadmap:feature"}
                              defaults={"Feature"}
                            />
                          </span>
                        </span>
                      </SelectItem>

                      <SelectItem value={"bug"}>
                        <span className={"flex items-center gap-1"}>
                          <BugIcon className={"text-destructive h-4"} />
                          <span>
                            <Trans i18nKey={"roadmap:bug"} defaults={"Bug"} />
                          </span>
                        </span>
                      </SelectItem>

                      <SelectItem value={"question"}>
                        <span className={"flex items-center gap-1"}>
                          <MessageCircleQuestion
                            className={"h-4 text-blue-500"}
                          />

                          <span>
                            <Trans
                              i18nKey={"roadmap:question"}
                              defaults={"Question"}
                            />
                          </span>
                        </span>
                      </SelectItem>

                      <SelectItem value={"task"}>
                        <span className={"flex items-center gap-1"}>
                          <CpuIcon className={"h-4 text-orange-500"} />
                          <span>
                            <Trans i18nKey={"roadmap:task"} defaults={"Task"} />
                          </span>
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
            name={"type"}
          />

          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey={"roadmap:title"} defaults={"Title"} />
                </FormLabel>

                <FormControl>
                  <Input placeholder="Enter a concise title" {...field} />
                </FormControl>

                <FormDescription>
                  <Trans
                    i18nKey={"roadmap:titleDescription"}
                    defaults={
                      "A concise title will help others understand your suggestion"
                    }
                  />
                </FormDescription>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans
                    i18nKey={"roadmap:description"}
                    defaults={"Description"}
                  />
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe your the issue in detail"
                    {...field}
                    rows={4}
                  />
                </FormControl>

                <FormDescription>
                  <Trans
                    i18nKey={"roadmap:descriptionDescription"}
                    defaults={
                      "Provide as much detail as possible to help others understand your suggestion or issue"
                    }
                  />
                </FormDescription>

                <FormMessage />
              </FormItem>
            )}
          />

          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>
              <Trans i18nKey={"roadmap:cancel"} defaults={"Cancel"} />
            </AlertDialogCancel>

            <Button disabled={pending || props.disabled} type="submit">
              <Trans i18nKey={"roadmap:submit"} defaults={"Submit"} />
            </Button>
          </AlertDialogFooter>
        </If>
      </form>
    </Form>
  );
}
